/**
 * The media library component: filter, search, drag-and-drop upload with
 * progress, and a grid of cards. Used in two modes:
 *
 * - `manage` (page `/lehrer/medien`): a click opens the preview with the
 *   board player, details, where it is used, and delete.
 * - `pick` (the picker dialog editors open): a click chooses the medium.
 *
 * Motion: cards grow in staggered; filter and delete reflow with the View
 * Transitions API where the browser has it; uploads show a growing vine
 * and the new card sprouts into the grid; a refused upload shakes.
 */
import {
  MEDIA_LIMITS,
  type MediaInfo,
  type MediaKind,
  type MediaUsage,
} from '@schulspiele/protocol';

import { api, ApiError } from '../api';
import { esc, qs } from '../dom';
import {
  formatBytes,
  formatDuration,
  KIND_LABEL,
  tooLarge,
  uploadMedia,
  UploadError,
} from '../media';
import { openDialog } from './dialog';
import { createMediaPlayer } from './media-player';
import { toast } from './toast';
import { withTransition } from './transition';
import './media-library.css';

export interface MediaLibraryOptions {
  mode: 'manage' | 'pick';
  /** Lock the filter to one kind (picker for "an image"). */
  kind?: MediaKind;
  onPick?: (media: MediaInfo) => void;
}

export interface MediaLibraryHandle {
  element: HTMLElement;
  destroy(): void;
}

type Filter = 'all' | MediaKind;

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'image', label: 'Bilder' },
  { value: 'audio', label: 'Ton' },
  { value: 'video', label: 'Video' },
];

const ACCEPT: Record<Filter, string> = {
  all: 'image/jpeg,image/png,image/webp,image/gif,audio/*,video/mp4,video/webm',
  image: 'image/jpeg,image/png,image/webp,image/gif',
  audio: 'audio/*',
  video: 'video/mp4,video/webm',
};

const AUDIO_ICON = `<svg viewBox="0 0 120 80" aria-hidden="true"><rect width="120" height="80" fill="#cfe3f0"/><g fill="#4f7a4e">${[
  8, 22, 34, 46, 30, 52, 40, 24, 36, 14,
]
  .map(
    (h, i) =>
      `<rect x="${String(14 + i * 9.5)}" y="${String(40 - h / 2)}" width="6" height="${String(h)}" rx="3"/>`,
  )
  .join('')}</g></svg>`;
const VIDEO_ICON = `<svg viewBox="0 0 120 80" aria-hidden="true"><rect width="120" height="80" fill="#1f3b2d"/><circle cx="60" cy="40" r="18" fill="#8fcf6a"/><path d="M54 30 L70 40 L54 50Z" fill="#1f3b2d"/></svg>`;

const PLACEHOLDER_LEAF = `<svg viewBox="0 0 40 40" aria-hidden="true"><path d="M20 3 C33 9 36 25 20 37 C4 25 7 9 20 3Z" fill="#8fcf6a" stroke="#1f3b2d" stroke-width="2"/><path d="M20 8 V34" stroke="#1f3b2d" stroke-width="1.6"/></svg>`;

function cardThumb(m: MediaInfo): string {
  if (m.kind === 'image' && m.thumbUrl !== null) {
    return `<img src="${esc(m.thumbUrl)}" alt="" loading="lazy" decoding="async" />`;
  }
  if (m.kind === 'video' && m.posterUrl !== null) {
    return `<img src="${esc(m.posterUrl)}" alt="" loading="lazy" decoding="async" /><span class="ml-card__badge">▶</span>`;
  }
  return m.kind === 'audio' ? AUDIO_ICON : VIDEO_ICON;
}

function cardMeta(m: MediaInfo): string {
  const parts = [KIND_LABEL[m.kind], formatBytes(m.bytes)];
  if (m.durationMs !== null) parts.push(formatDuration(m.durationMs));
  else if (m.width !== null && m.height !== null)
    parts.push(`${String(m.width)} × ${String(m.height)}`);
  return parts.join(' · ');
}

function cardHtml(m: MediaInfo, index: number): string {
  return `<li class="ml-card" data-id="${esc(m.id)}" style="view-transition-name: m-${esc(m.id)}; animation-delay: ${String(Math.min(index, 12) * 40)}ms">
    <button type="button" class="ml-card__button" aria-label="${esc(m.name)}, ${esc(KIND_LABEL[m.kind])}">
      <span class="ml-card__thumb">${cardThumb(m)}</span>
      <span class="ml-card__name">${esc(m.name)}</span>
      <span class="ml-card__meta">${esc(cardMeta(m))}</span>
    </button>
  </li>`;
}

export function createMediaLibrary(options: MediaLibraryOptions): MediaLibraryHandle {
  let items: MediaInfo[] = [];
  let filter: Filter = options.kind ?? 'all';
  let search = '';
  let loaded = false;
  const limitMb = (k: MediaKind): number => Math.round(MEDIA_LIMITS[k] / 1024 / 1024);

  const root = document.createElement('div');
  root.className = `ml ml--${options.mode}`;
  root.innerHTML = `
    <div class="ml-tools">
      ${
        options.kind === undefined
          ? `<div class="ml-filter" role="tablist" aria-label="Art">
               <span class="ml-filter__indicator" aria-hidden="true"></span>
               ${FILTERS.map((f) => `<button type="button" role="tab" data-filter="${f.value}" aria-selected="${String(f.value === filter)}">${f.label}</button>`).join('')}
             </div>`
          : ''
      }
      <label class="field ml-search"><span class="sr-only">Suchen</span><input type="search" placeholder="Nach Namen suchen" data-search /></label>
    </div>
    <label class="ml-drop" data-drop>
      <input type="file" multiple accept="${ACCEPT[filter]}" data-file hidden />
      <span class="ml-drop__art" aria-hidden="true">${PLACEHOLDER_LEAF}</span>
      <span class="ml-drop__text"><strong>Dateien hierher ziehen</strong> oder <u>auswählen</u></span>
      <span class="ml-drop__limits">Bilder bis ${String(limitMb('image'))} MB, Ton bis ${String(limitMb('audio'))} MB, Video bis ${String(limitMb('video'))} MB</span>
    </label>
    <ul class="ml-queue" data-queue aria-live="polite"></ul>
    <ul class="ml-grid" data-grid aria-busy="true"></ul>
    <div class="ml-empty" data-empty hidden></div>`;

  const grid = qs(root, '[data-grid]');
  const empty = qs(root, '[data-empty]');
  const queue = qs(root, '[data-queue]');
  const fileInput = qs<HTMLInputElement>(root, '[data-file]');
  const drop = qs(root, '[data-drop]');

  /* ---- Rendering --------------------------------------------------------- */

  const visible = (): MediaInfo[] =>
    items.filter(
      (m) =>
        (filter === 'all' || m.kind === filter) &&
        (search === '' || m.name.toLowerCase().includes(search)),
    );

  const render = (): void => {
    const list = visible();
    grid.removeAttribute('aria-busy');
    grid.innerHTML = list.map(cardHtml).join('');
    empty.hidden = !loaded || list.length > 0;
    empty.textContent =
      items.length === 0
        ? 'Noch keine Medien. Zieh die erste Datei in das Feld oben.'
        : 'Nichts gefunden. Anderer Filter oder anderer Suchbegriff?';
  };

  const moveIndicator = (): void => {
    const indicator = root.querySelector<HTMLElement>('.ml-filter__indicator');
    const active = root.querySelector<HTMLElement>(`[data-filter="${filter}"]`);
    if (indicator === null || active === null) return;
    indicator.style.width = `${String(active.offsetWidth)}px`;
    indicator.style.transform = `translateX(${String(active.offsetLeft)}px)`;
  };

  /* ---- Loading ----------------------------------------------------------- */

  const load = async (): Promise<void> => {
    const query = options.kind !== undefined ? `?kind=${options.kind}` : '';
    const res = await api<{ media: MediaInfo[] }>(`/api/media${query}`);
    items = res.media;
    loaded = true;
    render();
    requestAnimationFrame(moveIndicator);
  };

  /* ---- Filter and search ------------------------------------------------- */

  root.querySelector('.ml-filter')?.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-filter]');
    if (b === null) return;
    filter = (b.dataset.filter ?? 'all') as Filter;
    for (const x of root.querySelectorAll('[data-filter]')) {
      x.setAttribute('aria-selected', String(x === b));
    }
    fileInput.accept = ACCEPT[filter];
    moveIndicator();
    withTransition(render);
  });
  qs<HTMLInputElement>(root, '[data-search]').addEventListener('input', (e) => {
    search = (e.target as HTMLInputElement).value.trim().toLowerCase();
    render();
  });
  const onResize = (): void => {
    moveIndicator();
  };
  window.addEventListener('resize', onResize);

  /* ---- Upload ------------------------------------------------------------ */

  const pending: { file: File; item: HTMLElement }[] = [];
  let running = 0;
  const MAX_PARALLEL = 2;

  const pump = (): void => {
    while (running < MAX_PARALLEL && pending.length > 0) {
      const next = pending.shift();
      if (next === undefined) return;
      running += 1;
      void runUpload(next.file, next.item).finally(() => {
        running -= 1;
        pump();
      });
    }
  };

  const queueItem = (file: File): HTMLElement => {
    const li = document.createElement('li');
    li.className = 'ml-up';
    li.innerHTML = `
      <span class="ml-up__name">${esc(file.name)}</span>
      <span class="ml-up__size">${esc(formatBytes(file.size))}</span>
      <span class="ml-up__track"><span class="ml-up__vine"></span><span class="ml-up__leaf">${PLACEHOLDER_LEAF}</span></span>
      <span class="ml-up__status" data-status>Wartet …</span>
      <button type="button" class="ml-up__close" aria-label="Ausblenden" hidden>×</button>`;
    li.querySelector('.ml-up__close')?.addEventListener('click', () => {
      collapse(li);
    });
    queue.append(li);
    return li;
  };

  const setProgress = (li: HTMLElement, fraction: number): void => {
    li.style.setProperty('--p', String(fraction));
  };

  const collapse = (li: HTMLElement): void => {
    const h = li.offsetHeight;
    li.animate(
      [
        { height: `${String(h)}px`, opacity: 1 },
        { height: '0px', opacity: 0, marginTop: '0px', paddingTop: '0px', paddingBottom: '0px' },
      ],
      { duration: 320, easing: 'cubic-bezier(0.4, 0, 0.6, 1)' },
    ).finished.then(
      () => {
        li.remove();
      },
      () => {
        li.remove();
      },
    );
  };

  const runUpload = async (file: File, li: HTMLElement): Promise<void> => {
    const status = qs(li, '[data-status]');
    li.classList.add('is-uploading');
    status.textContent = 'Lädt hoch …';
    try {
      const info = await uploadMedia(file, (p) => {
        setProgress(li, p);
        if (p >= 1) status.textContent = 'Wird verarbeitet …';
      });
      li.classList.remove('is-uploading');
      li.classList.add('is-done');
      status.textContent = 'Fertig';
      items = [info, ...items];
      withTransition(() => {
        render();
        grid.querySelector(`[data-id="${CSS.escape(info.id)}"]`)?.classList.add('is-new');
      });
      window.setTimeout(() => {
        collapse(li);
      }, 900);
    } catch (err) {
      li.classList.remove('is-uploading');
      li.classList.add('is-error');
      status.textContent = err instanceof UploadError ? err.message : 'Hochladen fehlgeschlagen.';
      (li.querySelector('.ml-up__close') as HTMLElement).hidden = false;
    }
  };

  const addFiles = (files: FileList | File[]): void => {
    for (const file of files) {
      const li = queueItem(file);
      if (tooLarge(file)) {
        li.classList.add('is-error');
        qs(li, '[data-status]').textContent =
          'Zu groß. Bilder bis 10 MB, Töne bis 20 MB, Videos bis 200 MB.';
        (li.querySelector('.ml-up__close') as HTMLElement).hidden = false;
        continue;
      }
      pending.push({ file, item: li });
    }
    pump();
  };

  fileInput.addEventListener('change', () => {
    if (fileInput.files !== null) addFiles(fileInput.files);
    fileInput.value = '';
  });
  let dragDepth = 0;
  drop.addEventListener('dragenter', (e) => {
    e.preventDefault();
    dragDepth += 1;
    drop.classList.add('is-over');
  });
  drop.addEventListener('dragover', (e) => {
    e.preventDefault();
  });
  drop.addEventListener('dragleave', () => {
    dragDepth -= 1;
    if (dragDepth <= 0) drop.classList.remove('is-over');
  });
  drop.addEventListener('drop', (e) => {
    e.preventDefault();
    dragDepth = 0;
    drop.classList.remove('is-over');
    if (e.dataTransfer?.files !== undefined) addFiles(e.dataTransfer.files);
  });

  /* ---- Card clicks ------------------------------------------------------- */

  grid.addEventListener('click', (e) => {
    const card = (e.target as HTMLElement).closest<HTMLElement>('.ml-card');
    if (card === null) return;
    const media = items.find((m) => m.id === card.dataset.id);
    if (media === undefined) return;
    if (options.mode === 'pick') {
      card.classList.add('is-picked');
      options.onPick?.(media);
    } else {
      void openPreview(media);
    }
  });

  const removeCard = (id: string): void => {
    items = items.filter((m) => m.id !== id);
    withTransition(render);
  };

  const openPreview = async (media: MediaInfo): Promise<void> => {
    const player = createMediaPlayer({
      kind: media.kind,
      url: media.url,
      posterUrl: media.posterUrl,
      label: media.name,
      compact: true,
    });
    const body = document.createElement('div');
    body.className = 'ml-preview';
    const stage = document.createElement('div');
    stage.className = 'ml-preview__stage';
    stage.append(player.element);
    body.append(stage);
    body.insertAdjacentHTML(
      'beforeend',
      `<dl class="ml-preview__facts">
         <dt>Art</dt><dd>${esc(KIND_LABEL[media.kind])}</dd>
         <dt>Größe</dt><dd>${esc(formatBytes(media.bytes))}</dd>
         ${media.width !== null && media.height !== null ? `<dt>Maße</dt><dd>${String(media.width)} × ${String(media.height)} px</dd>` : ''}
         ${media.durationMs !== null ? `<dt>Länge</dt><dd>${esc(formatDuration(media.durationMs))}</dd>` : ''}
         <dt>Hochgeladen</dt><dd>${esc(new Date(media.createdAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }))}</dd>
       </dl>
       <p class="ml-preview__usage muted" data-usage>Prüfe, wo es verwendet wird …</p>
       <p class="error" data-error role="alert"></p>`,
    );
    const dialog = openDialog({
      title: media.name,
      body,
      className: 'dialog--wide',
      actions: [
        { label: 'Schließen', value: 'cancel', kind: 'ghost' },
        { label: 'Löschen', value: 'delete', kind: 'danger' },
      ],
      async onSubmit() {
        try {
          await api(`/api/media/${encodeURIComponent(media.id)}`, { method: 'DELETE' });
          toast('Gelöscht.', 'success');
          removeCard(media.id);
          return true;
        } catch (err) {
          const reason =
            err instanceof ApiError ? (err.body as { reason?: string } | null)?.reason : undefined;
          qs(body, '[data-error]').textContent = reason ?? 'Löschen fehlgeschlagen.';
          return false;
        }
      },
    });
    void api<MediaInfo & { usage: MediaUsage }>(`/api/media/${encodeURIComponent(media.id)}`)
      .then(({ usage }) => {
        const el = body.querySelector('[data-usage]');
        if (el === null) return;
        el.textContent =
          usage.sets.length === 0
            ? 'Wird in keinem Fragenset verwendet.'
            : `Verwendet in: ${usage.sets.map((s) => `„${s.title}“`).join(', ')}`;
      })
      .catch(() => undefined);
    await dialog.result;
    player.destroy();
  };

  void load().catch(() => {
    grid.removeAttribute('aria-busy');
    empty.hidden = false;
    empty.textContent = 'Die Medien konnten nicht geladen werden.';
  });

  return {
    element: root,
    destroy() {
      window.removeEventListener('resize', onResize);
      root.remove();
    },
  };
}

/**
 * Open the library as a picker. Resolves with the chosen medium or null
 * when cancelled. Uploading inside the picker works too, so a teacher can
 * add a picture while editing a question.
 */
export async function openMediaPicker(kind?: MediaKind): Promise<MediaInfo | null> {
  let picked: MediaInfo | null = null;
  const library = createMediaLibrary({
    mode: 'pick',
    ...(kind !== undefined ? { kind } : {}),
    onPick: (m) => {
      picked = m;
      dialog.close('picked');
    },
  });
  const title = kind === undefined ? 'Medium wählen' : `${KIND_LABEL[kind]} wählen`;
  const dialog = openDialog({
    title,
    body: library.element,
    className: 'dialog--wide dialog--tall',
    actions: [{ label: 'Abbrechen', value: 'cancel', kind: 'ghost' }],
  });
  await dialog.result;
  library.destroy();
  return picked;
}
